import { useState, useEffect } from "react";

import { Button, Modal, Table, message, InputNumber, AutoComplete, DatePicker, Radio } from "antd";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { ColumnsType } from "antd/es/table";
import { SourceCommision } from "../../@types/sourcecommision";
import { useTranslation } from "react-i18next";
import moment from "moment";

import axios from "axios";

interface SourceCommisionProps {
  visible: boolean;
  onCancel: () => void;
  RoomName: string | null;
  BusinessSegment?: string | boolean;
}

const SourceCommisionModal: React.FC<SourceCommisionProps> = (props) => {
  const emptyCommission = {
    RowID: "",
    RoomName: props.RoomName as string,
    BookingSource: "",
    SourceCommision: 0,
    ValidFrom: null,
    ValidUntil: null,
  };

  const user = useSelector((state: RootState) => state.auth.user);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Array<any>>([]);
  const [initialValues, setInitialValues] = useState<Array<SourceCommision>>([
    emptyCommission,
  ]);
  const [allCommissions, setAllCommissions] = useState<Array<SourceCommision>>([]);
  const [filterMode, setFilterMode] = useState<"active" | "all">("active");
  const [bookingSources, setBookingSources] = useState<Array<any>>([]);
  const [t] = useTranslation("common");

  const formSchema = Yup.object({
    commissions: Yup.array().of(
      Yup.object().shape({
        RowID: Yup.string(),
        RoomName: Yup.string().required("Required"),
        BookingSource: Yup.string().required("Required"),
        SourceCommision: Yup.number().required("Required"),
        ValidFrom: Yup.date().nullable(),
        ValidUntil: Yup.date().nullable(),
      })
    ),
  });

  const formik = useFormik({
    initialValues: {
      commissions: initialValues,
    },
    enableReinitialize: true,
    validationSchema: formSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        // set BookingSource to camel cased values

        const res = await axios
          .post(`/source-commisions`, { commissions: values.commissions })
          .then((res) => res.data);

        if (res?.success) {
          message.success(t("Saved successfully."));
          props.onCancel();
        }
      } catch (err: any) {
        console.log(err);
      }
      setSubmitting(false);
    },
  });

  const {
    values,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
    setSubmitting,
    isSubmitting,
    resetForm,
  } = formik;

  const columns: ColumnsType<SourceCommision> = [
    {
      title: t("apartments.sourceCommission.Room"),
      dataIndex: "RoomName",
      width: 120,
    },
    {
      title: t("apartments.sourceCommission.Booking Source"),
      dataIndex: "BookingSource",
      width: 150,
      render: (
        BookingSource: string,
        record: SourceCommision,
        index: number
      ) => {
        return (
          <AutoComplete
            value={BookingSource}
            className={`w-full ${
              errors.commissions &&
              touched.commissions &&
              touched.commissions[index]?.BookingSource &&
              Boolean((errors.commissions[index] as any)?.BookingSource) &&
              "border border-red-500"
            }`}
            disabled={user?.Role === "admin" || user?.Role === "super-admin" ? false : true}
            onChange={(value) =>
              setFieldValue(`commissions[${index}]BookingSource`, value)
            }
            options={bookingSources}
            filterOption={(inputValue, option: any) =>
              option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !==
              -1
            }
          />
        );
      },
    },
    {
      title: t("apartments.sourceCommission.Commission"),
      dataIndex: "SourceCommision",
      width: 100,
      render: (Commission: number, record: SourceCommision, index: number) => {
        return (
          <InputNumber
            placeholder="Commission"
            disabled={user?.Role === "admin" || user?.Role === "super-admin" ? false : true}
            className={`w-full h-10 ${
              errors.commissions &&
              touched.commissions &&
              touched.commissions[index]?.SourceCommision &&
              Boolean((errors.commissions[index] as any)?.SourceCommision) &&
              "border-red-500"
            }`}
            value={Commission}
            onChange={(value) =>
              setFieldValue(`commissions[${index}]SourceCommision`, value)
            }
            step="0.01"
          />
        );
      },
    },
    {
      title: "Valid From",
      dataIndex: "ValidFrom",
      width: 130,
      render: (date: any, record: SourceCommision, index: number) => {
        return (
          <DatePicker
            value={date ? moment(date) : null}
            disabled={user?.Role === "admin" || user?.Role === "super-admin" ? false : true}
            className={`w-full ${
              errors.commissions &&
              touched.commissions &&
              touched.commissions[index]?.ValidFrom &&
              Boolean((errors.commissions[index] as any)?.ValidFrom) &&
              "border-red-500"
            }`}
            onChange={(value) =>
              setFieldValue(`commissions[${index}]ValidFrom`, value?.toDate())
            }
            format="YYYY-MM-DD"
          />
        );
      },
    },
    {
      title: "Valid Until",
      dataIndex: "ValidUntil",
      width: 130,
      render: (date: any, record: SourceCommision, index: number) => {
        return (
          <DatePicker
            value={date ? moment(date) : null}
            disabled={user?.Role === "admin" || user?.Role === "super-admin" ? false : true}
            className={`w-full ${
              errors.commissions &&
              touched.commissions &&
              touched.commissions[index]?.ValidUntil &&
              Boolean((errors.commissions[index] as any)?.ValidUntil) &&
              "border-red-500"
            }`}
            onChange={(value) =>
              setFieldValue(`commissions[${index}]ValidUntil`, value?.toDate())
            }
            format="YYYY-MM-DD"
          />
        );
      },
    },
  ];

  const addCommission = () => {
    setFieldValue("commissions", [...values.commissions, emptyCommission]);
  };

  const confirmDelete = () => {
    Modal.confirm({
      title: (
        <div className="text-white text-center">
          {t("Do you want to delete")} ?
        </div>
      ),
      okText: t("YES"),
      icon: null,
      cancelText: t("NO"),
      width: 340,
      okButtonProps: {
        className: "btn-yellow hvr-float-shadow w-32 h-10 text-xs ml-3.5",
      },
      cancelButtonProps: {
        className: "btn-danger hvr-float-shadow w-32 h-10 text-xs",
      },
      onOk: async () => {
        const commissionIds = selectedRowKeys
          .map((key) => key.split("_")[0])
          .filter((id) => Boolean(id));

        setSubmitting(true);
        try {
          if (commissionIds.length > 0) {
            const res = await axios
              .delete("/source-commisions", { data: { ids: commissionIds } })
              .then((res) => res.data);
            if (res?.success) {
              message.success(t("Deleted successfully."));
            }
          }
        } catch (err) {
          console.log(err);
        }

        const commissions = values.commissions.filter(
          (row, index) =>
            selectedRowKeys.indexOf(`${row.RowID}_${index}`) === -1
        );
        setFieldValue("commissions", commissions);

        setSubmitting(false);
      },
      onCancel() {},
    });
  };

  const deleteCommisions = async () => {
    if (selectedRowKeys.length === 0) {
      message.warning(t("Select commissions first."));
      return;
    }

    confirmDelete();
  };

  const fetchCommissions = async (segments?:any) => {
    try {
      const res = await axios
        .get("/source-commisions/list", {
          params: {
            RoomName: props.RoomName,
          },
        })
        .then((res)=>{ return res.data; });

      const mappedCommissions = res.map((row: any) => {
        if(segments){
          var bs = segments.filter((elm:any)=>{
                return elm.BookingSource.toLowerCase() === row?.BookingSource.toLowerCase();
          });
          var SourceCommision = row.SourceCommision;
          if(bs.length>0){
            SourceCommision = bs[0].SourceCommision;
          }
          return {
            ...row,
            BookingSource: row?.BookingSource?.toLowerCase(),
            SourceCommision,
            ValidFrom: row.ValidFrom,
            ValidUntil: row.ValidUntil,
          };
        }else{
          return {
            ...row,
            BookingSource: row?.BookingSource?.toLowerCase(),
            ValidFrom: row.ValidFrom,
            ValidUntil: row.ValidUntil,
          };
        }
      });

      setAllCommissions(mappedCommissions);
      applyFilter(mappedCommissions, filterMode);
    } catch (err) {
      console.log(err);
    }
  };

  const applyFilter = (commissions: SourceCommision[], mode: "active" | "all") => {
    if (mode === "active") {
      const today = new Date();
      const filtered = commissions.filter((row: any) => {
        const validFrom = row.ValidFrom ? new Date(row.ValidFrom) : null;
        const validUntil = row.ValidUntil ? new Date(row.ValidUntil) : null;

        if (!validFrom && !validUntil) return true; // Show if no dates set
        if (!validFrom) return today <= validUntil!;
        if (!validUntil) return today >= validFrom;

        return today >= validFrom && today <= validUntil;
      });
      setInitialValues(filtered);
    } else {
      setInitialValues(commissions);
    }
  };

  const handleFilterChange = (mode: "active" | "all") => {
    setFilterMode(mode);
    applyFilter(allCommissions, mode);
  };

  const fetchBookingSources = async () => {
    try {
      const res = await axios
        .get("/source-commisions/booking-sources")
        .then((res) => res.data);

      setBookingSources(
        res.map((row: any) => {
          return { value: row?.BookingSource };
        })
      );
    } catch (err) {
      console.log(err);
    }
  };

  const fetchDefaultSourceCommisions = async (segment:string)=>{


    const res = await axios
    .get("/source-commisions/default-list")
    .then((res) => res.data);
    
     var b_arr = res.filter((itm:any)=>{
      return itm.BusinessSegment === segment;
    });

    return b_arr;
  }


  useEffect(() => {
    if (props.visible) {
      if(props.BusinessSegment !== false){
      var link = props.BusinessSegment;
      if(typeof link != "string"){
        link = "";
      }
      fetchDefaultSourceCommisions(link).then((res)=>{
      fetchCommissions(res);
      fetchBookingSources();
      });
      }else{
      fetchCommissions();
      fetchBookingSources();
      }
    } else {
      resetForm();
      setSelectedRowKeys([]);
      setFilterMode("active");
    }
  }, [props.visible]);

  return (
    <Modal
      closable={true}
      visible={props.visible}
      footer={false}
      width={900}
      centered
      className="modal-body-p-0 close-outside"
      onCancel={() => props.onCancel()}
    >
      <form
        onSubmit={handleSubmit}
        className="flex flex-col items-center bg-white"
      >
        <div className="w-full px-5 pt-4 pb-2 flex justify-between items-center border-b">
          <h3 className="text-base font-semibold">Source Commissions</h3>
          <Radio.Group
            value={filterMode}
            onChange={(e) => handleFilterChange(e.target.value)}
            buttonStyle="solid"
            size="small"
          >
            <Radio.Button value="active">Active</Radio.Button>
            <Radio.Button value="all">All</Radio.Button>
          </Radio.Group>
        </div>
        <Table
          rowKey={(record, index) => {
            return `${record.RowID}_${index}`;
          }}
          rowSelection={{
            selectedRowKeys,
            onChange: (values) => setSelectedRowKeys(values),
          }}
          columns={columns}
          dataSource={values.commissions}
          className="w-full table-sm"
          rowClassName="cursor-pointer hover:bg-c-blue hover:bg-opacity-10"
          pagination={false}
        />

        <div
          className={`flex mt-5 mb-3 ${user?.Role === "admin" || user?.Role === "super-admin" ? "" : "hidden"}`}
        >
          <Button
            key="delete"
            className="btn-danger hvr-float-shadow h-9 w-36 ml-3.5 text-xs"
            onClick={deleteCommisions}
            disabled={isSubmitting}
          >
            {t("DELETE")}
          </Button>
          <Button
            key="add"
            onClick={addCommission}
            className="btn-dark hvr-float-shadow h-9 w-36 ml-3.5 px-0 text-xs"
            disabled={isSubmitting}
          >
            {t("apartments.sourceCommission.ADD COMMISSION")}
          </Button>
          <Button
            key="save"
            htmlType="submit"
            disabled={isSubmitting}
            className="btn-yellow hvr-float-shadow h-9 w-36 ml-2 text-xs"
          >
            {t("SAVE")}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default SourceCommisionModal;
